/**
 * DosyaAyarlariPage.jsx — Sistem Ayarları › Dosya Ayarları (backlog #162, 2026-10-11)
 * Route: /Management/SistemAyarlari/Dosya   Roller: [1,2] (yazma yetkisi backend'de: SYSTEM_SETTINGS_MANAGE)
 *
 * Her yükleme kategorisi için en büyük dosya boyutu (MB) ve kabul edilen uzantılar — Sistem Ayarları'nın
 * `dosya` grubu (SystemSettingConstant.DOSYA_KATEGORILERI). Yükleme sırasında okunur; yeniden başlatma
 * gerekmez. Uzantılar yalnızca kategorinin güvenli seçenekleri arasından seçilir (html, svg, exe… hiçbir
 * kategoride seçilemez). Sunucunun kendi tavanı (spring.servlet.multipart.max-file-size) ayar değildir:
 * bir ayar onu aşarsa etkin sınır yine tavandır, sayfa bunu gösterir. Her değişiklik geçmişe yazılır.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Card, CardBody, CardHeader, Table, Button, Badge, Alert, Spinner, Input, InputGroup, InputGroupText,
} from "reactstrap";
import { Save, RotateCcw, AlertTriangle } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  listSystemSettings, updateSystemSetting, getDosyaOzet, SETTING_GROUP_DOSYA,
} from "../../../services/systemSettingsService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

const fmtDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? String(d) : date.toLocaleString(currentLocaleTag());
};
const ayni = (a, b) => JSON.stringify(a) === JSON.stringify(b);

function KategoriSatiri({ kategori, ayarlar, sunucuSiniriMb, onSaved }) {
  const { t } = useTranslation("ayarlar");
  const mbAyari = ayarlar[kategori.maxMbKey];
  const uzantiAyari = kategori.uzantiKey ? ayarlar[kategori.uzantiKey] : null;
  const [mb, setMb] = useState(String(mbAyari?.value ?? ""));
  const [uzantilar, setUzantilar] = useState(uzantiAyari?.value ?? kategori.uzantilar);
  const [kaydediyor, setKaydediyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [kaydedildi, setKaydedildi] = useState(false);

  // Kaydedilen değer değişince (burada ya da varsayılana dönünce) girdileri yeniden doldur — React'in
  // "prop değişince state'i ayarla" kalıbı, effect değil.
  const kaynak = JSON.stringify([mbAyari?.value, uzantiAyari?.value]);
  const [kaynakIcin, setKaynakIcin] = useState(kaynak);
  if (kaynakIcin !== kaynak) {
    setKaynakIcin(kaynak);
    setMb(String(mbAyari?.value ?? ""));
    setUzantilar(uzantiAyari?.value ?? kategori.uzantilar);
  }

  const mbDegisti = Number(mb) !== Number(mbAyari?.value);
  const uzantiDegisti = uzantiAyari ? !ayni(uzantilar, uzantiAyari.value) : false;
  const degisti = mbDegisti || uzantiDegisti;
  const tavanUstunde = Number(mb) > Number(sunucuSiniriMb);
  const varsayilan = mbAyari?.isDefault && (!uzantiAyari || uzantiAyari.isDefault);
  const sonDegisiklik = [mbAyari, uzantiAyari].filter((a) => a?.updatedAt)
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0];

  function uzantiDegistir(u, secili) {
    const secenekler = uzantiAyari?.secenekler || [];
    const yeni = new Set(uzantilar);
    if (secili) yeni.add(u); else yeni.delete(u);
    setUzantilar(secenekler.filter((s) => yeni.has(s)));
  }

  async function kaydet(mbDeger, uzantiDeger) {
    setKaydediyor(true);
    setHata(null);
    setKaydedildi(false);
    try {
      if (Number(mbDeger) !== Number(mbAyari?.value)) await updateSystemSetting(kategori.maxMbKey, Number(mbDeger));
      if (uzantiAyari && !ayni(uzantiDeger, uzantiAyari.value)) await updateSystemSetting(kategori.uzantiKey, uzantiDeger);
      setKaydedildi(true);
      onSaved();
    } catch (e) {
      setHata(e.message);
    } finally {
      setKaydediyor(false);
    }
  }

  return (
    <tr>
      <td style={{ minWidth: 220 }}>
        <div className="fw-semibold">
          {kategori.ad} {varsayilan && <Badge color="light" className="text-muted ms-1">{t("dosyaAyarlari.varsayilan")}</Badge>}
        </div>
        <div className="small text-muted">{kategori.aciklama}</div>
        {sonDegisiklik && (
          <div className="small text-muted mt-1">
            {t("dosyaAyarlari.sonDegisiklik")}{" "}{sonDegisiklik.updatedByName || `#${sonDegisiklik.updatedBy}`} · {fmtDate(sonDegisiklik.updatedAt)}
          </div>
        )}
        {hata && <Alert color="danger" className="small py-1 px-2 mt-2 mb-0">{hata}</Alert>}
        {kaydedildi && !degisti && <div className="small text-success mt-1">{t("dosyaAyarlari.kaydedildi")}</div>}
      </td>
      <td style={{ width: 170 }}>
        <InputGroup size="sm">
          <Input type="number" min={mbAyari?.min ?? 0.5} max={mbAyari?.max ?? 100} step="0.5" value={mb}
                 disabled={kaydediyor} onChange={(e) => setMb(e.target.value)} />
          <InputGroupText>{t("dosyaAyarlari.mb")}</InputGroupText>
        </InputGroup>
        {tavanUstunde && (
          <div className="small text-warning mt-1 d-flex gap-1">
            <AlertTriangle size={12} className="mt-1 flex-shrink-0" />{t("dosyaAyarlari.sunucuEnFazlaMbKabul", { sunucuSiniriMb })}
          </div>
        )}
        <div className="small text-muted mt-1">{t("dosyaAyarlari.etkinMb", { etkinMaxMb: kategori.etkinMaxMb })}</div>
      </td>
      <td>
        {uzantiAyari ? (
          <div className="d-flex flex-wrap gap-2">
            {(uzantiAyari.secenekler || []).map((u) => (
              <label key={u} className="d-inline-flex align-items-center gap-1 small mb-0">
                <Input type="checkbox" className="mt-0" checked={uzantilar.includes(u)} disabled={kaydediyor}
                       onChange={(e) => uzantiDegistir(u, e.target.checked)} />
                .{u}
              </label>
            ))}
          </div>
        ) : (
          <span className="small text-muted">{kategori.uzantilar.map((u) => `.${u}`).join(", ")}{" "}{t("dosyaAyarlari.sabit")}</span>
        )}
      </td>
      <td className="text-end text-nowrap" style={{ width: 110 }}>
        <Button size="sm" color="primary" className="me-1" disabled={kaydediyor || !degisti || !mb}
                title={t("common:kaydet")} onClick={() => kaydet(mb, uzantilar)}>
          {kaydediyor ? <Spinner size="sm" /> : <Save size={14} />}
        </Button>
        <Button size="sm" color="light" disabled={kaydediyor || varsayilan} title={t("dosyaAyarlari.varsayilanaDon")}
                onClick={() => kaydet(mbAyari?.defaultValue, uzantiAyari ? uzantiAyari.defaultValue : uzantilar)}>
          <RotateCcw size={14} />
        </Button>
      </td>
    </tr>
  );
}

export default function DosyaAyarlariPage() {
  const { t } = useTranslation("ayarlar");
  useEffect(() => { document.title = i18n.t("ayarlar:dosyaAyarlari.dosyaAyarlariFds"); }, []);

  const [ozet, setOzet] = useState(null);
  const [ayarListesi, setAyarListesi] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [yenile, setYenile] = useState(0);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setHata(null);
      try {
        const [o, liste] = await Promise.all([getDosyaOzet(), listSystemSettings(SETTING_GROUP_DOSYA)]);
        if (ignore) return;
        setOzet(o);
        setAyarListesi(liste);
      } catch (e) {
        if (!ignore) setHata(e.message);
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [yenile]);

  const ayarlar = useMemo(() => Object.fromEntries(ayarListesi.map((a) => [a.key, a])), [ayarListesi]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("dosyaAyarlari.dosyaAyarlari")} pageTitle={t("dosyaAyarlari.sistemAyarlari")} />
        {hata && <Alert color="danger">{hata}</Alert>}
        <Card>
          <CardHeader>
            <h5 className="mb-0">{t("dosyaAyarlari.dosyaAyarlari")}</h5>
            <small className="text-muted">
              {t("dosyaAyarlari.yuklemeKategorisiBasinaEnBuyuk")}
            </small>
          </CardHeader>
          <CardBody>
            {yukleniyor ? <Spinner size="sm" /> : ozet && (
              <>
                <Alert color="info" className="small py-2">
                  {t("dosyaAyarlari.sunucununKendiSiniriDosyaBasina")}{" "}<strong>{ozet.sunucuDosyaSiniriMb}{" "}{t("dosyaAyarlari.mb")}</strong>{t("dosyaAyarlari.istekBasina")}
                  <strong>{ozet.sunucuIstekSiniriMb}{" "}{t("dosyaAyarlari.mb")}</strong>{t("dosyaAyarlari.buIkisiAyarDegildirSunucu")}
                </Alert>
                <div className="table-responsive">
                  <Table size="sm" className="align-middle mb-0">
                    <thead>
                      <tr><th>{t("dosyaAyarlari.kategori")}</th><th>{t("dosyaAyarlari.enBuyukDosya")}</th><th>{t("dosyaAyarlari.uzantilar")}</th><th /></tr>
                    </thead>
                    <tbody>
                      {ozet.kategoriler.map((k) => (
                        <KategoriSatiri key={k.kod} kategori={k} ayarlar={ayarlar}
                                        sunucuSiniriMb={ozet.sunucuDosyaSiniriMb}
                                        onSaved={() => setYenile((n) => n + 1)} />
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}
